import { Model } from '../Model';
import { labelToFunction } from '../Functions';
import { checkValueType } from '../Util';
import { loadData } from './';


/**
 * Load a ready-to-use Component from the saved details
 * 
 * @param {JSON} componentJSON JSON with the saved component specificiations
 */
export function loadComponentParameters(componentJSON) {
  let output = {
    'label': componentJSON.label,
    'data': loadData(componentJSON.data),
    'method': loadMethod(componentJSON.method),
    'inputMapping': componentJSON.inputMapping
  };

  if (output.method) {
    output.scenarios = loadScenarios(output.method, componentJSON.scenarios)
  }

  return output;
}


/**
 * Load the actual js function based on the provided JSON using the
 * pre-defined mapping from name-to-function to mitigate security risks
 * 
 * @param {JSON} methodJSON JSON with the saved method specifications
 */
export function loadMethod(methodJSON) {
  if (methodJSON && methodJSON.label in labelToFunction) {
    return labelToFunction[methodJSON.label];
  } else {
    return null;
  }
}


/**
 * Check if all the values in a scenario match the expected paramter types
 * 
 * @param {Any[]} scenario Array of parameters to be passed into the method
 * @param {Object[]} parameters Array of {name, type} objects describing the
 *    expected paramters for a method
 */
export function allTypesValid(scenario, parameters) {
  for (let key in scenario) {
    let opt = scenario[key];
    let param = parameters.find(p => p.name === key);
    let paramType = (param ?? {}).type;

    if (!checkValueType(opt, paramType)) return false;
  }
  return true;
}


/**
 * Load a parsed set of scenarios from the saved details
 * 
 * @param {Function} method Already-parsed method function
 * @param {JSON} scenariosJSON JSON with the saved scenario specifications
 */
export function loadScenarios(method, scenariosJSON) {
  let outputScenarios = [];
  let params = method.parameters;

  for (let scenario of scenariosJSON) {
    if (params.length === 0) continue;
    if (Object.keys(scenario).length > params.length) continue;
    if (!allTypesValid(scenario, params)) continue;
    outputScenarios.push(scenario);
  }

  return outputScenarios;
}


/**
 * Load a ready-to-use model from the saved details
 * 
 * @param {JSON} modelJSON JSON with the entire saved model specifications
 */
export function loadModel(modelJSON) {
  let model = new Model(modelJSON.label);

  for (let componentJSON of modelJSON.components) {
    let component = loadComponentParameters(componentJSON);
    let c = model.addComponent(component.label);
    
    if (component.data) {
      c.setData(component.data);
    } else if (component.scenarios) {
      c.updateMethod(component.method, component.inputMapping);
      for (let s of component.scenarios) {
        c.addScenario(s);
      }
    } else {
      c.updateMethod(component.method, component.inputMapping);
    }
  }

  for (let name in modelJSON.dag) {
    model.addInputsToComponent(name, modelJSON.dag[name]);
  }

  for (let node in modelJSON.nodes) {
    model.nodes.addToNode(node, modelJSON.nodes[node]);
  }

  return model;
}